import type { Rng } from '../../../core/rng';
import { OPENING, doorAt, gableOpenings, windowSize, type Opening } from '../openings';
import { HOUSE } from '../houseStyle';

/** A stretch of wall, in wall coordinates, that no opening may come near. */
export interface ClearRect {
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
}

/** One wall of a free-standing civic building, between heights y0 and y1. */
export interface CivicWall {
  readonly length: number;
  readonly y0: number;
  readonly y1: number;
  /** The building's eaves, which set its storeys. */
  readonly eaveY: number;
  /** Whether the entrance is in this wall. */
  readonly door: boolean;
  readonly keepClear: readonly ClearRect[];
}

const CLEAR_MARGIN = 0.3;
const EPS = 1e-6;

/** The storey height that divides the eaves height nearest to a house storey. */
export function civicStorey(eaveY: number): number {
  return eaveY / Math.max(1, Math.round(eaveY / HOUSE.storeyHeight.mean));
}

/**
 * The storey line nearest `share` of the eaves height, leaving a storey above and below it,
 * so a tall ground floor ends where the windows of the storeys over it begin.
 */
export function storeyLineNear(eaveY: number, share: number): number {
  const storey = civicStorey(eaveY);
  const storeys = Math.round(eaveY / storey);
  if (storeys < 2) return share * eaveY;
  return storey * Math.min(storeys - 1, Math.max(1, Math.round((share * eaveY) / storey)));
}

/** Storey lines whose whole storey lies within the wall's heights. */
function storeyLines(wall: CivicWall, storey: number): number[] {
  const count = Math.round(wall.eaveY / storey);
  return Array.from({ length: count }, (_, k) => k * storey).filter(
    (line) => line >= wall.y0 - EPS && line + storey <= wall.y1 + EPS
  );
}

function clearOf(o: Opening, rects: readonly ClearRect[]): boolean {
  const m = CLEAR_MARGIN;
  return rects.every(
    (r) => o.x >= r.x1 + m || o.x + o.width <= r.x0 - m || o.y >= r.y1 + m || o.y + o.height <= r.y0 - m
  );
}

/**
 * STYLE §8.1 on a wall with no neighbour: a bay every 3.3 m, without the four-bay limit of a
 * house front, a window per bay and storey, and the door in an end bay of the ground floor.
 */
export function civicOpenings(wall: CivicWall, rng: Rng): Opening[] {
  const storey = civicStorey(wall.eaveY);
  const bays = Math.max(1, Math.round(wall.length / OPENING.bayModule));
  const bay = wall.length / bays;
  const size = windowSize(bay, storey);
  const doorBay = !wall.door ? -1 : bays === 1 || rng.fork('door').chance(0.5) ? 0 : bays - 1;
  const openings = storeyLines(wall, storey).flatMap((line) =>
    Array.from({ length: bays }, (_, b): Opening => {
      const centre = (b + 0.5) * bay;
      if (line === 0 && b === doorBay) return doorAt(centre, storey);
      return { kind: 'window', x: centre - size.width / 2, y: line + OPENING.sill, width: size.width, height: size.height };
    })
  );
  return openings.filter((o) => clearOf(o, wall.keepClear));
}

/** Small windows up the gable over a civic wall, placed as on a house's back gable. */
export function civicGable(width: number, eaveY: number, pitch: number): Opening[] {
  const storey = civicStorey(eaveY);
  return gableOpenings({ width, storeys: Math.round(eaveY / storey), storeyHeight: storey, pitch }, 'smallWindow');
}
